import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { isEquityOpen, startMarketClock, onMarketClock } from "../lib/marketClock";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// The NYSE holiday set that used to live here moved to lib/marketClock.js
// (one clock for every component; the backend's /omega/market-status can
// also close the session for early closes). public/index.html's FOUC-guard
// copy has to stay inline — it runs before any bundle loads.

/**
 * Top-level theme controller. Mounted by <App /> so it's active on EVERY route
 * (Dashboard, /mobile swipe-cards, AuthCallback, login page).
 *
 * Single source of truth for `document.documentElement[data-theme]`:
 *   • reads `zi_theme` from localStorage on mount (default: "auto")
 *   • resolves "auto" → "light" during NYSE hours, "dark" otherwise (refreshes every 60 s)
 *   • listens for the `zi-theme-changed` window event (fired by Mobile Settings + Dashboard SettingsTab)
 *   • persists every change to localStorage and the user state API
 *
 * Other components dispatch the event instead of touching the DOM directly:
 *   window.dispatchEvent(new CustomEvent("zi-theme-changed", { detail: { theme: "dark" } }))
 */
export default function ThemeManager() {
  const [theme, setTheme] = useState(() => {
    if (typeof window === "undefined") return "auto";
    return localStorage.getItem("zi_theme") || "auto";
  });

  const apply = useCallback((t) => {
    if (typeof document === "undefined") return;
    let resolved = t;
    if (t === "auto") {
      resolved = isEquityOpen() ? "light" : "dark";
    }
    // Only mutate the DOM if the resolved theme actually changed — avoids
    // gratuitous repaints (visible as a brief flicker on some GPUs).
    const current = document.documentElement.getAttribute("data-theme");
    if (current !== resolved) {
      document.documentElement.setAttribute("data-theme", resolved);
    }
    const targetBg = resolved === "light" ? "#f8f9fb" : "#060a12";
    if (document.body && document.body.style.background !== targetBg) {
      document.body.style.background = targetBg;
    }
    // Keep <html> bg in sync with the FOUC-guard inline script in index.html
    // so the very first paint matches the post-hydration state.
    if (document.documentElement.style.background !== targetBg) {
      document.documentElement.style.background = targetBg;
    }
  }, []);

  // Apply on mount + whenever theme changes; in `auto` mode poll only every
  // 5 minutes (not 60s) and only re-paint when the resolved value actually
  // changed — prevents bg flicker during paint cycles around the market
  // open/close boundary.
  useEffect(() => {
    apply(theme);
    if (theme !== "auto") return undefined;
    const iv = setInterval(() => { apply("auto"); }, 5 * 60 * 1000);
    return () => clearInterval(iv);
  }, [theme, apply]);

  // Keep a ref to the current theme so the handler can guard no-ops without
  // closing over stale state. Without the ref, [theme] in the dep array causes
  // the listener to tear down and re-register on every theme change, which
  // creates a brief window where a simultaneous event can be missed.
  const themeRef = useRef(theme);
  useEffect(() => { themeRef.current = theme; }, [theme]);

  // ThemeManager is mounted on every route, so it owns the shared market
  // clock's 60s poll; a server-side close (holiday, early close) repaints
  // an "auto" theme as soon as it lands instead of at the next 5-min tick.
  useEffect(() => {
    const stop = startMarketClock(API);
    const unsub = onMarketClock(() => { if (themeRef.current === "auto") apply("auto"); });
    return () => { unsub(); stop(); };
  }, [apply]);

  // Listen for zi-theme-changed — fired from MobileSettingsExpanded + Dashboard SettingsTab.
  useEffect(() => {
    const h = (e) => {
      const t = e?.detail?.theme;
      if (!t || t === themeRef.current) return;
      setTheme(t);
      try { localStorage.setItem("zi_theme", t); } catch {}
      // Persist server-side (no-op if not authenticated)
      axios.put(`${API}/user/state`, { theme: t }).catch(() => {});
    };
    window.addEventListener("zi-theme-changed", h);
    return () => window.removeEventListener("zi-theme-changed", h);
  }, []);

  return null;
}
